/**
 * Auth Token Manager
 *
 * Manages access token retrieval and storage for API requests
 * Integrates with Auth0 for token management
 */

type TokenGetter = () => Promise<string>;

let getTokenCallback: TokenGetter | null = null;

/**
 * Set the callback function to retrieve auth tokens
 * This should be called during app initialization with the Auth0 getAccessToken function
 *
 * @param callback - Function that returns a promise resolving to the access token
 */
export function setTokenGetter(callback: TokenGetter): void {
  getTokenCallback = callback;
}

/**
 * Get the current access token
 * Returns null if no token getter is set or if token retrieval fails
 *
 * @returns Access token or null
 */
export async function getAccessToken(): Promise<string | null> {
  if (!getTokenCallback) {
    return null;
  }

  try {
    const token = await getTokenCallback();
    // Return null if empty string (user not authenticated)
    return token || null;
  } catch (error) {
    // Silently return null - errors are logged in AuthContext
    return null;
  }
}

/**
 * Check if authentication is configured
 *
 * @returns true if token getter is set
 */
export function isAuthConfigured(): boolean {
  return getTokenCallback !== null;
}
